/* ==========================================================================
   WAYPOINT — App shell + hand-rolled router.

   Routes:
     /login          — night PIN login, no shell chrome
     /                — Trips (Home)
     /log             — The Log (flat cross-trip timeline of stays,
                        activities, and memories + insights; own nav tab)
     /stays           — legacy alias, redirects to /log
     /trips/:id       — Trip Detail
     /today           — Now: the travel-day screen (own nav tab since 2026-09-05)
     /scooty          — Scooty (shared agent + human thread, ?trip=<id>;
                        reached from the More tray, which carries its badge)
     /map             — Map
     /settings        — Settings (appearance, feature flags; own nav tab)
     /memories        — Memories (deep-link only since the Log took the tab;
                        the Log's "Memories" filter is the everyday path in)
     /bucket-list     — Bucket list (standing "some day" places; reached from
                        the More tray, surfaced per-trip on Trip Detail)

     Journal = where we write. A journal entry and a blog post are ONE row;
     publishing is the only difference (docs/JOURNAL-BLOG-MEMORIES.md §1).
     These four moved off /posts/* on 2026-09-06 so the word in the URL is
     the word in the UI; /posts/* still resolves, see the redirect below.

     /journal         — Journal list — Public and Private sections, and the
                        public blog's real URL (was /posts)
     /journal/new     — composer — write/paste, photo tray, save a draft
     /journal/:id     — entry screen — preview, tap-to-edit, drag-to-reorder,
                        Publish/Unpublish, and the entry's permalink
     /journal/:id/edit— the composer again, on an existing draft
     /journal/archive — the RETIRED nightly write-ups (docs/PLACE-NOTES.md
                        §8). Was /journal until the writing surface took the
                        name; linked only from the foot of the Journal list.
     /posts…          — legacy aliases, redirected to the /journal twin

     /s/:slug         — public SharePage — NO auth gate, NO bottom nav

     NOT a client route: /blog and /blog/:slug are the PUBLIC blog, rendered
     as real HTML by server/src/routes/blog.ts and mounted ahead of the SPA
     fallback. The app opens them in a new tab; never navigate() there.

   Feature screens are lazy-loaded from src/features/* and
   src/pages/SharePage.tsx per the ownership map in docs/BLUEPRINT.md.
   ========================================================================== */

import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { MotionConfig } from 'framer-motion';
import { useLocation, matchRoute, navigate } from './lib/router';
import { checkAuth, useStoreSelector } from './lib/store';
import { Login } from './screens/Login';
import { Screen } from './shell/Screen';
import { AppErrorBoundary } from './shell/AppErrorBoundary';
import { BottomNav } from './shell/BottomNav';
import { Shimmer, SHIMMER_CSS } from './features/trips/components/Shimmer';

// Every lazy import is hoisted to a named factory so it can ALSO be called
// directly — by the idle preloader below, and by loaderFor() which holds a
// navigation until the target's chunk is in memory (see useResolvedPath).
const importTripsHome = () => import('./features/trips');
const importTripDetail = () => import('./features/trips/TripDetail');
const importLog = () => import('./features/log');
const importToday = () => import('./features/today');
const importJournalArchive = () => import('./features/trips/Journal');
const importScooty = () => import('./features/scooty');
const importMap = () => import('./features/map');
const importMemories = () => import('./features/memories');
const importBucketList = () => import('./features/bucketlist');
const importPostsList = () => import('./features/posts');
const importPostScreen = () => import('./features/posts/PostScreen');
const importComposer = () => import('./features/posts/Composer');
const importSettings = () => import('./features/settings');

const TripsHome = lazy(importTripsHome);
const TripDetail = lazy(importTripDetail);
const LogScreen = lazy(importLog);
const TodayScreen = lazy(importToday);
const JournalArchiveScreen = lazy(importJournalArchive);
const ScootyScreen = lazy(importScooty);
const MapScreen = lazy(importMap);
const MemoriesScreen = lazy(importMemories);
const BucketListScreen = lazy(importBucketList);
const PostsListScreen = lazy(importPostsList);
const PostScreen = lazy(importPostScreen);
const Composer = lazy(importComposer);
const SettingsScreen = lazy(importSettings);
const SharePage = lazy(() => import('./pages/SharePage'));

/** Which Journal screen a path is, or null if it is not in the /journal subtree.
 *
 *  One function, used by BOTH loaderFor and AuthedShell below, because the ordering here is a
 *  trap and it is not one you want written out twice: '/journal/archive' and '/journal/new' both
 *  satisfy the '/journal/:id' pattern, so the literal paths MUST be tested first. Getting that
 *  wrong renders the entry screen against an id of "archive", which fetches a post that does not
 *  exist — a 404 on a link the app itself printed. Exported for web/src/App.routes.test.ts.
 *
 *  Query strings never reach here: this router keys off window.location.pathname only. */
export type JournalRoute =
  | { kind: 'list' }
  | { kind: 'archive' }
  | { kind: 'new' }
  | { kind: 'edit'; id: string }
  | { kind: 'entry'; id: string };

export function journalSubroute(path: string): JournalRoute | null {
  if (path === '/journal') return { kind: 'list' };
  if (path === '/journal/archive') return { kind: 'archive' };
  if (path === '/journal/new') return { kind: 'new' };
  const edit = matchRoute('/journal/:id/edit', path);
  if (edit) return { kind: 'edit', id: edit.id };
  const entry = matchRoute('/journal/:id', path);
  if (entry) return { kind: 'entry', id: entry.id };
  return null;
}

/** '/posts…' -> its '/journal…' twin, or null when this is not a legacy posts path. The writing
 *  surface was renamed on 2026-09-06 (docs/JOURNAL-BLOG-MEMORIES.md §2) and every old address —
 *  a bookmark, a link Scooty sent, a note in Jeff's phone — still has to open the same thing. */
export function legacyPostsPath(path: string): string | null {
  if (path !== '/posts' && !path.startsWith('/posts/')) return null;
  return `/journal${path.slice('/posts'.length)}`;
}

/** The chunk a path needs before its screen can render. Keep in step with the branches in
 *  AuthedShell — a path that falls through to TripsHome there must resolve here too. */
function loaderFor(path: string): () => Promise<unknown> {
  if (path === '/log') return importLog;
  if (path === '/today') return importToday;
  if (path.startsWith('/scooty')) return importScooty;
  if (path === '/map') return importMap;
  if (path === '/settings') return importSettings;
  if (path === '/memories') return importMemories;
  if (path === '/bucket-list') return importBucketList;
  const journal = journalSubroute(path);
  if (journal) {
    if (journal.kind === 'archive') return importJournalArchive;
    if (journal.kind === 'list') return importPostsList;
    if (journal.kind === 'new' || journal.kind === 'edit') return importComposer;
    return importPostScreen;
  }
  if (matchRoute('/trips/:id', path)) return importTripDetail;
  return importTripsHome;
}

/** Holds the rendered route one step behind the URL until the target's lazy chunk is actually
 *  in memory.
 *
 *  Originally this existed to keep `AnimatePresence mode="wait"` from being handed a key it
 *  could not render. That AnimatePresence is gone (see the render below for why), so this is no
 *  longer load-bearing for correctness — the Suspense boundary inside `Screen` handles a cold
 *  chunk on its own now. It stays because it is still the nicer behavior: rather than flashing
 *  ScreenSkeleton for the length of one chunk fetch, the current screen holds for that beat and
 *  then the real content slides in. The URL updates instantly either way, so the nav highlights
 *  and back/forward stay honest.
 *
 *  Note the deliberate lack of a timeout: `import()` settles or rejects, and the reject path is
 *  caught below and allowed to advance, so this cannot wedge the app the way its predecessor
 *  could. The worst case is the old screen holding for one same-origin fetch. */
function useResolvedPath(path: string): string {
  const [resolved, setResolved] = useState(path);

  useEffect(() => {
    if (path === resolved) return;
    let cancelled = false;
    loaderFor(path)()
      // A failed chunk fetch must still advance, or the app wedges on the old screen forever —
      // let it through and letAppErrorBoundary/Suspense surface it on render instead.
      .catch(() => {})
      .then(() => {
        if (!cancelled) setResolved(path);
      });
    return () => {
      cancelled = true;
    };
  }, [path, resolved]);

  return resolved;
}

/** Warms every bottom-nav tab's chunk once the first screen is idle. There are only four, they
 *  are small, and the user is going to open them — paying for them during idle time buys away the
 *  double-skeleton flash on every first tab switch of a session. Falls back to a timeout where
 *  requestIdleCallback isn't available (Safari). */
function preloadTabChunks(): () => void {
  const warm = () => {
    void importTripsHome();
    void importLog();
    void importScooty();
    void importMap();
  };
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  }).requestIdleCallback;
  if (typeof ric === 'function') {
    const handle = ric(warm, { timeout: 3000 });
    return () => (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(handle);
  }
  const timer = window.setTimeout(warm, 1200);
  return () => window.clearTimeout(timer);
}

// Lazy-route Suspense fallback for every screen — shaped like a generic
// screen (header line + a couple of card blocks) rather than one static
// grey bar, and animated with the same shimmer sweep every other skeleton
// in the app uses, so a cold navigation to Map/Journal/Memories doesn't
// flash three visually distinct layouts in a row (this bar -> the screen's
// own richer skeleton -> content).
function ScreenSkeleton() {
  return (
    <div style={{ padding: 'calc(20px + var(--sat)) 20px 0', maxWidth: 640, margin: '0 auto' }} aria-hidden="true">
      <Shimmer height={34} width="55%" />
      <div style={{ marginTop: 20 }}>
        <Shimmer height={140} radius={22} />
      </div>
      <div style={{ marginTop: 12 }}>
        <Shimmer height={140} radius={22} />
      </div>
      <style>{SHIMMER_CSS}</style>
    </div>
  );
}

function AuthedShell({ path }: { path: string }) {
  // Render the LAST FULLY-LOADABLE path, not the raw URL — see useResolvedPath.
  const path_ = useResolvedPath(path);
  const tripMatch = matchRoute('/trips/:id', path_);
  const journal = journalSubroute(path_);

  let content: ReactNode;
  if (path_ === '/' || path_ === '') {
    content = <TripsHome />;
  } else if (tripMatch) {
    content = <TripDetail params={{ id: tripMatch.id }} />;
  } else if (path_ === '/log') {
    content = <LogScreen />;
  } else if (path_ === '/today') {
    content = <TodayScreen />;
  } else if (path_.startsWith('/scooty')) {
    content = <ScootyScreen />;
  } else if (path_ === '/map') {
    content = <MapScreen />;
  } else if (path_ === '/settings') {
    content = <SettingsScreen />;
  } else if (path_ === '/memories') {
    content = <MemoriesScreen />;
  } else if (path_ === '/bucket-list') {
    content = <BucketListScreen />;
  } else if (journal) {
    // Ordering lives in journalSubroute, not here — see the trap documented on it.
    if (journal.kind === 'list') content = <PostsListScreen />;
    else if (journal.kind === 'archive') content = <JournalArchiveScreen />;
    else if (journal.kind === 'new') content = <Composer params={{}} />;
    else if (journal.kind === 'edit') content = <Composer params={{ id: journal.id }} />;
    else content = <PostScreen params={{ id: journal.id }} />;
  } else {
    content = <TripsHome />;
  }

  return (
    <>
      {/* NO AnimatePresence around the screen swap. This is the third and final fix for "tap a
          tab, get a blank page, only a reload brings it back", and the first one that removes the
          mechanism rather than dodging it.

          `AnimatePresence mode="wait"` removes the outgoing child through a handshake that is not
          crash-proof (framer-motion 11.18.2, AnimatePresence/index.mjs):

            const onExit = () => {
              if (exitComplete.has(key)) exitComplete.set(key, true)
              else return                                  // <-- silent, permanent bail
              ...
              setRenderedChildren(pendingPresentChildren.current)   // the ONLY removal path
            }

          `exitComplete` is seeded in AnimatePresence's own layout effect, but `onExit` is fired by
          the CHILD's animation. Child effects run before parent effects, and React 18 can re-render
          or discard a render in between — so an exit that finishes on the wrong side of that
          window calls `onExit` with an empty map, returns early, and `setRenderedChildren` is never
          called again. Under `mode="wait"` the component then renders ONLY the exiting child,
          forever: it sits at its exit end-state (opacity 0), so the page is blank, and no
          subsequent route can ever mount. Navigating back to the stranded screen's own path
          re-enters that same node, which is why exactly one tab keeps "working" while every other
          tab is blank — the tell that identified this.

          BottomNav hit the identical wedge with the More sheet and fixed it the same way (see the
          comment there). The cost is the 180ms slide-OUT; the enter animation is untouched, and
          since only one screen is ever mounted there is no double-height CLS either — which was
          the reason `mode="wait"` was chosen in the first place.

          Suspense stays INSIDE Screen: with the boundary outside, a cold lazy chunk suspends the
          subtree containing the animated wrapper, so framer never runs the enter animation and the
          element is stranded at `initial` (opacity 0) — a different route to the same blank page.
          Inside, the wrapper always mounts and animates; only its children suspend. */}
      <Screen key={path_} path={path_}>
        {/* Backstop only. useResolvedPath means the chunk is normally already resolved by the
            time this mounts, so the fallback should not render in practice — but a boundary
            still has to exist for a lazy child, and it belongs inside the animated wrapper. */}
        <AppErrorBoundary>
          <Suspense fallback={<ScreenSkeleton />}>{content}</Suspense>
        </AppErrorBoundary>
      </Screen>
      <BottomNav />
    </>
  );
}

export default function App() {
  const path = useLocation();
  const authUser = useStoreSelector((s) => s.authUser);
  const authChecked = useStoreSelector((s) => s.authChecked);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!authUser) return;
    return preloadTabChunks();
  }, [authUser]);

  const shareMatch = matchRoute('/s/:slug', path);
  const isLoginRoute = path === '/login';

  // Redirects are side effects, not render output — doing them inline in
  // the render body triggers React's "setState while rendering a different
  // component" warning (navigate() synchronously updates the router store).
  useEffect(() => {
    if (shareMatch || !authChecked) return;
    // '/stays' was the flat ledger's address before it became the Log. Kept as a redirect so an
    // existing bookmark or a link in an old chat message still lands somewhere real.
    if (path === '/stays') {
      navigate('/log', { replace: true });
      return;
    }
    // '/posts…' was the writing surface's address until it was renamed Journal
    // (docs/JOURNAL-BLOG-MEMORIES.md §2). Every path maps one-for-one onto its /journal twin, so
    // an old bookmark, a link Scooty sent, or a note in Jeff's phone still opens the same entry.
    const legacy = legacyPostsPath(path);
    if (legacy) {
      navigate(legacy, { replace: true });
      return;
    }
    if (isLoginRoute && authUser) {
      navigate('/', { replace: true });
    } else if (!isLoginRoute && !authUser) {
      navigate('/login', { replace: true });
    }
  }, [shareMatch, authChecked, isLoginRoute, authUser, path]);

  // Public share pages bypass auth entirely and never show app chrome.
  let body: ReactNode;
  if (shareMatch) {
    body = (
      <Suspense fallback={null}>
        <SharePage params={{ slug: shareMatch.slug }} />
      </Suspense>
    );
  } else if (isLoginRoute) {
    body = authChecked && authUser ? null : <Login />; // else: redirect effect above will navigate away
  } else if (!authChecked || !authUser) {
    // Avoid a flash of the wrong screen while the initial /api/auth/me
    // check is in flight, or while the redirect-to-login effect runs.
    body = <div style={{ minHeight: '100dvh', background: 'var(--canvas)' }} />;
  } else {
    body = <AuthedShell path={path} />;
  }

  // Applied once, here, so every framer-motion animation in the app —
  // including ones outside this ownership boundary (map, chat, replay) —
  // picks up prefers-reduced-motion instead of relying on each component
  // remembering to gate itself by hand. This is additive to (not a
  // replacement for) the manual opacity-fade gates already used by
  // TripDetail's parallax, Screen's shared-axis transition, the modal
  // sheets, and Login's PIN shake: MotionConfig's reducedMotion="user"
  // only freezes "unsafe" transform/layout values in place (no fade) when
  // reduced motion is on, whereas those hand-rolled branches produce a real
  // opacity fade — deleting them in favor of relying on MotionConfig alone
  // would be a regression, not a cleanup, so they stay.
  // The outer boundary is the backstop for the shell itself (login, the share
  // page, the redirect effects). Screens get their own, one level down, so a
  // crash inside a route keeps the bottom nav — see AuthedShell.
  return (
    <MotionConfig reducedMotion="user">
      <AppErrorBoundary>{body}</AppErrorBoundary>
    </MotionConfig>
  );
}
